import { useState, useRef, useEffect } from 'react';
import { categories, categoryImages } from '@/mocks/categories';

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener,nofollow');
    setDropdownOpen(false);
  };

  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="w-full px-4 md:px-10 max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
            Browse <strong className="text-primary-600">Pandabuy Sheet</strong> by Category
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Select a category from the dropdown below or click any category button to explore curated pandabuy spreadsheet finds
          </p>
        </div>

        <div className="flex justify-center mb-10" ref={dropdownRef}>
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-100 border border-background-300/60 rounded-xl text-sm font-medium font-label text-foreground-800 hover:border-primary-400 transition-all duration-200 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-base text-primary-500"></i>
                Select a Category
              </span>
              <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 border border-background-200/70 rounded-xl shadow-lg z-40 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer first:rounded-t-xl last:rounded-b-xl"
                  >
                    <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${cat.colorClass}`}>
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col bg-background-100 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 hover:shadow-sm transition-all duration-200 cursor-pointer"
            >
              <div className="relative w-full aspect-square overflow-hidden bg-background-200">
                <img
                  src={categoryImages[cat.name]}
                  alt={`${cat.name} pandabuy spreadsheet category`}
                  title={`PandabuySpreadsheet ${cat.name} Category`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              </div>
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${cat.colorClass}`}>
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                <span className="text-xs font-medium text-foreground-700 leading-tight">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}